// src/components/ActionsPanel.jsx
//
// ACTIONS-LAYER-S1: Full-width pop-down panel for the actions queue.
//
// Opens below the header bar (position: fixed below the header, full width).
// Lists open actions grouped by urgency: Overdue → This Week → Later.
// Each row has:
//   - Urgency chip (red / amber / gray)
//   - Type chip (decision / follow_up)
//   - Title + context (truncated)
//   - Due date (if set)
//   - Owner
//   - Complete / Dismiss buttons
//
// Permission enforcement:
//   - Viewer: sees Mine tab only; no Complete/Dismiss buttons (read-only).
//   - Editor+: Mine / All tabs; Complete and Dismiss buttons active.
//
// Auto-close: panel closes when all open actions are resolved.
//
// Flow:
//   1. On open: fetch /api/actions?scope=mine (and optionally scope=all).
//   2. Complete: PATCH /api/actions/:id/complete with outcome (required for
//      decisions, optional for follow_ups). Removes row inline.
//   3. Dismiss: PATCH /api/actions/:id/dismiss with resolution_reason.
//      Shows inline reason prompt before submitting.
//   4. When list empties: shows empty-state and auto-closes after 2 s.

import React, { useState, useEffect, useCallback, useRef } from "react";
import { T } from "../app/tokens.js";

// ─── Constants ────────────────────────────────────────────────────────────────

const URGENCY_CONFIG = {
  overdue:   { label: "Overdue",   bg: T.redSoft,   fg: T.red,           chip: T.utilRed    },
  this_week: { label: "This Week", bg: T.utilAmber,  fg: T.actionAmberFg, chip: T.ragAmberBg },
  later:     { label: "Later",     bg: T.hoverBg,    fg: T.mid,           chip: T.lightGray  },
};

const TYPE_LABEL = {
  decision:  "Decision",
  follow_up: "Follow-up",
};

const URGENCY_ORDER = ["overdue", "this_week", "later"];

// ─── Inline dismiss prompt ────────────────────────────────────────────────────

function DismissPrompt({ onSubmit, onCancel }) {
  const [reason, setReason] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      marginTop: 8, padding: "8px 10px",
      background: T.dismissPromptBg, border: `1px solid ${T.border}`,
      borderRadius: 6,
    }}>
      <input
        ref={inputRef}
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason for dismissal…"
        data-testid="dismiss-reason-input"
        onKeyDown={(e) => {
          if (e.key === "Enter" && reason.trim()) onSubmit(reason.trim());
          if (e.key === "Escape") onCancel();
        }}
        style={{
          flex: 1,
          border: `1px solid ${T.border}`,
          borderRadius: 4,
          padding: "4px 8px",
          fontSize: T.fsSm,
          color: T.dark,
        }}
      />
      <button
        type="button"
        onClick={() => reason.trim() && onSubmit(reason.trim())}
        disabled={!reason.trim()}
        data-testid="dismiss-confirm-btn"
        style={{
          padding: "4px 10px",
          fontSize: T.fsSm,
          fontWeight: T.fwSemibold,
          background: T.red,
          color: T.white,
          border: "none",
          borderRadius: 4,
          cursor: reason.trim() ? "pointer" : "not-allowed",
          opacity: reason.trim() ? 1 : 0.5,
        }}
      >
        Dismiss
      </button>
      <button
        type="button"
        onClick={onCancel}
        data-testid="dismiss-cancel-btn"
        style={{
          padding: "4px 8px",
          fontSize: T.fsSm,
          color: T.mid,
          background: "transparent",
          border: "none",
          cursor: "pointer",
        }}
      >
        Cancel
      </button>
    </div>
  );
}

// ─── Inline complete prompt (for decisions that require outcome) ──────────────

function CompletePrompt({ action, onSubmit, onCancel }) {
  const isDecision = action.type === "decision";
  const [outcome, setOutcome] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  function handleSubmit() {
    if (isDecision && !outcome.trim()) return;
    onSubmit(isDecision ? outcome.trim() : outcome.trim() || null);
  }

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      marginTop: 8, padding: "8px 10px",
      background: T.acceptedRowBg, border: `1px solid ${T.greenBadge}`,
      borderRadius: 6,
    }}>
      {isDecision ? (
        <input
          ref={inputRef}
          type="text"
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
          placeholder="Record the decision outcome…"
          data-testid="complete-outcome-input"
          onKeyDown={(e) => {
            if (e.key === "Enter" && outcome.trim()) handleSubmit();
            if (e.key === "Escape") onCancel();
          }}
          style={{
            flex: 1,
            border: `1px solid ${T.border}`,
            borderRadius: 4,
            padding: "4px 8px",
            fontSize: T.fsSm,
            color: T.dark,
          }}
        />
      ) : (
        <span style={{ flex: 1, fontSize: T.fsSm, color: T.mid }}>
          Mark this follow-up as done?
        </span>
      )}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={isDecision && !outcome.trim()}
        data-testid="complete-confirm-btn"
        style={{
          padding: "4px 10px",
          fontSize: T.fsSm,
          fontWeight: T.fwSemibold,
          background: T.green,
          color: T.white,
          border: "none",
          borderRadius: 4,
          cursor: (isDecision && !outcome.trim()) ? "not-allowed" : "pointer",
          opacity: (isDecision && !outcome.trim()) ? 0.5 : 1,
        }}
      >
        Done
      </button>
      <button
        type="button"
        onClick={onCancel}
        data-testid="complete-cancel-btn"
        style={{
          padding: "4px 8px",
          fontSize: T.fsSm,
          color: T.mid,
          background: "transparent",
          border: "none",
          cursor: "pointer",
        }}
      >
        Cancel
      </button>
    </div>
  );
}

// ─── Source-object navigation URL builder ────────────────────────────────────
//
// Source-object type icon — quick visual cue on each row.
const SOURCE_ICON = {
  location: "🏢",
  lease:    "📋",
  signal:   "🔔",
};

// Builds the ?-param URL that App.jsx reads to navigate to a source object.
// Supported sourceObjectType values: 'location', 'lease', 'signal'.

function sourceNavUrl(action) {
  const p = new URLSearchParams({
    actionNav:   action.sourceObjectType,
    actionNavId: action.sourceObjectId,
    actionId:    action.id,
  });
  if (action.sourceLocationId) p.set("actionNavLocId", action.sourceLocationId);
  return `?${p.toString()}`;
}

// ─── Action row ───────────────────────────────────────────────────────────────

function ActionRow({ action, canEdit, onRemoved, onNavigate }) {
  const [showing, setShowing] = useState(null); // null | 'complete' | 'dismiss'
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);

  const urg = URGENCY_CONFIG[action.urgency] ?? URGENCY_CONFIG.later;

  async function patchAction(path, body) {
    setSaving(true);
    setError(null);
    try {
      const r = await fetch(`/api/actions/${action.id}/${path}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      onRemoved(action.id);
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  }

  function handleComplete(outcome) {
    patchAction("complete", { outcome: outcome ?? undefined });
  }

  function handleDismiss(reason) {
    patchAction("dismiss", { resolution_reason: reason });
  }

  return (
    <div
      data-testid={`action-row-${action.id}`}
      style={{
        padding: "12px 16px",
        borderBottom: `1px solid ${T.border}`,
        background: T.card,
      }}
    >
      {/* Top row: chips + title */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
        {/* Urgency chip */}
        <span style={{
          display: "inline-block",
          padding: "2px 7px",
          borderRadius: 10,
          background: urg.chip,
          color: urg.fg,
          fontSize: T.fsXs,
          fontWeight: T.fwSemibold,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}>
          {urg.label}
        </span>

        {/* Type chip */}
        <span style={{
          display: "inline-block",
          padding: "2px 7px",
          borderRadius: 10,
          background: T.actionTypeChipBg,
          color: T.blue,
          fontSize: T.fsXs,
          fontWeight: T.fwSemibold,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}>
          {TYPE_LABEL[action.type] ?? action.type}
        </span>

        {/* Source type icon */}
        <span
          data-testid={`source-icon-${action.id}`}
          title={action.sourceObjectType}
          style={{ fontSize: T.fsBase, flexShrink: 0 }}
          aria-hidden="true"
        >
          {SOURCE_ICON[action.sourceObjectType] ?? "•"}
        </span>

        {/* Title */}
        <span style={{
          fontSize: T.fsBase,
          fontWeight: T.fwSemibold,
          color: T.dark,
          lineHeight: 1.4,
          flex: 1,
          minWidth: 0,
          wordBreak: "break-word",
        }}>
          {action.title}
        </span>

        {/* Action buttons */}
        {canEdit && showing === null && (
          <div style={{ display: "flex", gap: 6, flexShrink: 0, marginLeft: "auto" }}>
            <button
              type="button"
              disabled={saving}
              data-testid={`complete-btn-${action.id}`}
              onClick={() => setShowing("complete")}
              style={{
                padding: "4px 10px",
                fontSize: T.fsXs,
                fontWeight: T.fwSemibold,
                background: T.green,
                color: T.white,
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              ✓ Complete
            </button>
            <button
              type="button"
              disabled={saving}
              data-testid={`dismiss-btn-${action.id}`}
              onClick={() => setShowing("dismiss")}
              style={{
                padding: "4px 10px",
                fontSize: T.fsXs,
                fontWeight: T.fwSemibold,
                background: T.card,
                color: T.mid,
                border: `1px solid ${T.border}`,
                borderRadius: 4,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              Dismiss
            </button>
          </div>
        )}
      </div>

      {/* Context line */}
      {action.context && (
        <p style={{
          margin: "4px 0 0",
          fontSize: T.fsSm,
          color: T.mid,
          lineHeight: 1.4,
          wordBreak: "break-word",
        }}>
          {action.context}
        </p>
      )}

      {/* Meta row: owner + due date + "Open ›" deep-link */}
      <div style={{ display: "flex", gap: 12, marginTop: 6, flexWrap: "wrap", alignItems: "center" }}>
        {action.owner && (
          <span style={{ fontSize: T.fsXs, color: T.mid }}>
            Owner: {action.owner}
          </span>
        )}
        {action.dueDate && (
          <span style={{ fontSize: T.fsXs, color: urg.fg }}>
            Due: {action.dueDate}
          </span>
        )}
        {/* "Open ›" navigates to the source object. Uses URL param
            to communicate intent to App; panel closes immediately. */}
        <button
          type="button"
          data-testid={`open-source-btn-${action.id}`}
          onClick={() => {
            if (onNavigate) {
              onNavigate({
                type:             action.sourceObjectType,
                id:               action.sourceObjectId,
                sourceLocationId: action.sourceLocationId ?? null,
                actionId:         action.id,
                url:              sourceNavUrl(action),
              });
            }
          }}
          style={{
            marginLeft: "auto",
            padding: "2px 0",
            fontSize: T.fsXs,
            color: T.blue,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            fontWeight: T.fwSemibold,
            textDecoration: "underline",
          }}
        >
          Open ›
        </button>
      </div>

      {/* Error */}
      {error && (
        <p style={{ marginTop: 6, fontSize: T.fsXs, color: T.red }}>
          {error}
        </p>
      )}

      {/* Inline prompts */}
      {showing === "complete" && (
        <CompletePrompt
          action={action}
          onSubmit={handleComplete}
          onCancel={() => setShowing(null)}
        />
      )}
      {showing === "dismiss" && (
        <DismissPrompt
          onSubmit={handleDismiss}
          onCancel={() => setShowing(null)}
        />
      )}
    </div>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {boolean} props.open          — controlled visibility
 * @param {()=>void} props.onClose      — close callback
 * @param {string} [props.role]         — 'viewer' | 'editor' | 'admin'
 * @param {number} [props.headerHeight] — px offset from top (default 60)
 * @param {(src:{type:string,id:string,url:string})=>void} [props.onNavigate]
 *   Called when "Open ›" is clicked on an action row. Receives the source
 *   object type, id, and a pre-built URL param string. The panel closes
 *   immediately; the caller handles navigation.
 */
export function ActionsPanel({ open, onClose, role = "viewer", headerHeight = 60, onNavigate }) {
  const [scope,          setScope]          = useState("mine");
  const [typeFilter,     setTypeFilter]     = useState(null); // null | 'decision' | 'follow_up'
  const [locationFilter, setLocationFilter] = useState("");   // location ID string or ""
  const [locInputOpen,   setLocInputOpen]   = useState(false);
  const [actions,        setActions]        = useState([]);
  const [mineCount,      setMineCount]      = useState(0);
  const [allCount,       setAllCount]       = useState(0);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState(null);

  const canEdit = role === "editor" || role === "admin";
  const canAll  = canEdit;

  // Auto-close timer ref.
  const autoCloseRef = useRef(null);

  const fetchActions = useCallback(async (sc = scope, tf = typeFilter, lf = locationFilter) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ scope: sc });
      if (tf) params.set("type", tf);
      if (lf) params.set("location_id", lf);
      const r = await fetch(`/api/actions?${params.toString()}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setActions(data.actions ?? []);
      setMineCount(data.mineCount ?? 0);
      setAllCount(data.allCount ?? 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [scope, typeFilter]);

  // Fetch on open, scope change, type filter change, or location filter change.
  useEffect(() => {
    if (!open) return;
    fetchActions(scope, typeFilter, locationFilter);
  }, [open, scope, typeFilter, locationFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-close when all actions are resolved.
  useEffect(() => {
    if (!open || loading) return;
    if (actions.length === 0 && !error) {
      autoCloseRef.current = setTimeout(() => onClose(), 2000);
    }
    return () => clearTimeout(autoCloseRef.current);
  }, [actions.length, open, loading, error, onClose]);

  // Remove action from local list when completed/dismissed.
  function handleRemoved(id) {
    setActions((prev) => prev.filter((a) => a.id !== id));
    setMineCount((c) => Math.max(0, c - 1));
    setAllCount((c) => Math.max(0, c - 1));
  }

  if (!open) return null;

  // Group by urgency in canonical order.
  const grouped = {};
  for (const u of URGENCY_ORDER) grouped[u] = [];
  for (const a of actions) {
    const u = URGENCY_ORDER.includes(a.urgency) ? a.urgency : "later";
    grouped[u].push(a);
  }

  const hasAny = actions.length > 0;

  return (
    <>
      {/* Backdrop (click outside to close) */}
      <div
        data-testid="actions-panel-backdrop"
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          top: headerHeight,
          zIndex: 1100,
          background: "transparent",
        }}
      />

      {/* Panel */}
      <div
        data-testid="actions-panel"
        role="dialog"
        aria-label="Actions queue"
        aria-modal="false"
        data-feedback-obscuring-overlay="true"
        style={{
          position: "fixed",
          top: headerHeight,
          left: 0,
          right: 0,
          zIndex: 1101,
          background: T.card,
          borderBottom: `1px solid ${T.border}`,
          boxShadow: T.shadowFloat,
          maxHeight: "60vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header bar */}
        <div style={{
          display: "flex",
          alignItems: "center",
          padding: "10px 24px",
          borderBottom: `1px solid ${T.border}`,
          gap: 12,
          flexShrink: 0,
          background: T.hoverBg,
        }}>
          <span style={{
            fontSize: T.fsMd,
            fontWeight: T.fwBold,
            color: T.dark,
          }}>
            Actions
          </span>

          {/* Scope tabs (editor+ only) */}
          {canAll && (
            <div style={{ display: "flex", gap: 4, marginLeft: 8 }}>
              {[
                { key: "mine", label: `Mine (${mineCount})` },
                { key: "all",  label: `All (${allCount})` },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  data-testid={`scope-tab-${key}`}
                  onClick={() => setScope(key)}
                  style={{
                    padding: "4px 12px",
                    fontSize: T.fsSm,
                    fontWeight: scope === key ? T.fwSemibold : 400,
                    color: scope === key ? T.blue : T.mid,
                    background: scope === key ? T.blueSoft : "transparent",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* Separator between scope tabs and type chips (editor+ only) */}
          {canAll && (
            <span aria-hidden="true" style={{
              width: 1,
              height: 16,
              background: T.border,
              alignSelf: "center",
              flexShrink: 0,
            }} />
          )}

          {/* Type filter chips — Decisions / Follow-ups / All types */}
          <div style={{ display: "flex", gap: 4, marginLeft: canAll ? 0 : 8 }}>
            {[
              { key: null,        label: "All types",    testId: "filter-chip-all" },
              { key: "decision",  label: "Decisions",    testId: "filter-chip-decisions" },
              { key: "follow_up", label: "Follow-ups",   testId: "filter-chip-follow-ups" },
            ].map(({ key, label, testId }) => {
              const active = typeFilter === key;
              return (
                <button
                  key={String(key)}
                  type="button"
                  data-testid={testId}
                  onClick={() => setTypeFilter(key)}
                  style={{
                    padding: "3px 10px",
                    fontSize: T.fsXs,
                    fontWeight: active ? T.fwSemibold : 400,
                    color: active ? T.dark : T.mid,
                    background: active ? T.border : "transparent",
                    border: `1px solid ${active ? T.mid : "transparent"}`,
                    borderRadius: 12,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* By-location filter */}
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <button
              type="button"
              data-testid="filter-chip-by-location"
              onClick={() => {
                if (locationFilter) {
                  setLocationFilter("");
                  setLocInputOpen(false);
                } else {
                  setLocInputOpen((o) => !o);
                }
              }}
              style={{
                padding: "3px 10px",
                fontSize: T.fsXs,
                fontWeight: locationFilter ? T.fwSemibold : 400,
                color: locationFilter ? T.dark : T.mid,
                background: locationFilter ? T.border : "transparent",
                border: `1px solid ${locationFilter ? T.mid : "transparent"}`,
                borderRadius: 12,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {locationFilter ? `📍 ${locationFilter}` : "By location ▾"}
            </button>
            {locInputOpen && !locationFilter && (
              <input
                data-testid="location-filter-input"
                type="text"
                placeholder="Location ID"
                autoFocus
                style={{
                  fontSize: T.fsXs,
                  padding: "3px 8px",
                  border: `1px solid ${T.border}`,
                  borderRadius: 6,
                  width: 120,
                }}
                onBlur={(e) => {
                  if (!e.target.value) setLocInputOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const v = e.target.value.trim();
                    if (v) { setLocationFilter(v); setLocInputOpen(false); }
                    else    { setLocInputOpen(false); }
                  }
                  if (e.key === "Escape") { setLocInputOpen(false); }
                }}
              />
            )}
          </div>

          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            {loading && (
              <span style={{ fontSize: T.fsXs, color: T.mid }}>Loading…</span>
            )}
            <button
              type="button"
              data-testid="actions-panel-close"
              aria-label="Close actions panel"
              onClick={onClose}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: T.mid,
                fontSize: T.fsMd,
                lineHeight: 1,
                padding: "2px 4px",
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Body */}
        <div
          data-testid="actions-panel-body"
          style={{ overflowY: "auto", flex: 1 }}
        >
          {error && (
            <div style={{ padding: "16px 24px", color: T.red, fontSize: T.fsSm }}>
              Failed to load actions: {error}
            </div>
          )}

          {!loading && !error && !hasAny && (
            <div
              data-testid="actions-empty-state"
              style={{
                padding: "32px 24px",
                textAlign: "center",
                color: T.mid,
                fontSize: T.fsBase,
              }}
            >
              <div style={{ fontSize: T.fsXl, marginBottom: 8 }}>✓</div>
              All caught up - no open actions
            </div>
          )}

          {URGENCY_ORDER.map((urgency) => {
            const rows = grouped[urgency] ?? [];
            if (!rows.length) return null;
            const urg = URGENCY_CONFIG[urgency];
            return (
              <div key={urgency}>
                {/* Urgency group header */}
                <div style={{
                  padding: "6px 16px",
                  background: urg.chip,
                  borderBottom: `1px solid ${T.border}`,
                  fontSize: T.fsXs,
                  fontWeight: T.fwBold,
                  color: urg.fg,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}>
                  {urg.label} · {rows.length}
                </div>
                {rows.map((action) => (
                  <ActionRow
                    key={action.id}
                    action={action}
                    canEdit={canEdit}
                    onRemoved={handleRemoved}
                    onNavigate={onNavigate ? (src) => { onClose(); onNavigate(src); } : undefined}
                  />
                ))}
              </div>
            );
          })}
        </div>

        {/* Footer count */}
        {!loading && hasAny && (
          <div style={{
            padding: "8px 24px",
            borderTop: `1px solid ${T.border}`,
            fontSize: T.fsXs,
            color: T.mid,
            background: T.hoverBg,
            flexShrink: 0,
          }}>
            {actions.length} action{actions.length !== 1 ? "s" : ""} open
          </div>
        )}
      </div>
    </>
  );
}
